import React from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { useDispatch } from "react-redux";
import Transition from "./Transition";
import { setDialog } from "../../features/dialog/dialogSlice";
import { useUpdateMemberMutation } from "../../features/members/membersApiSlice";
import Iconify from "../iconify/Iconify";
import {
  MEMBER_STATUS_OPTIONS,
  getMemberStatusConfig,
  normalizeMemberStatus,
} from "../../utils/memberStatus";

function EditStatusDialog({ dialog, handleClose }) {
  const dispatch = useDispatch();
  const member = dialog?.data || {};
  const [newStatus, setNewStatus] = React.useState(
    normalizeMemberStatus(member?.status) || "active",
  );
  const [errorMessage, setErrorMessage] = React.useState("");
  const [updateMember, { isLoading }] = useUpdateMemberMutation();

  const currentStatus = getMemberStatusConfig(member?.status);
  const selectedStatus = getMemberStatusConfig(newStatus);
  const hasStatusChanged =
    normalizeMemberStatus(member?.status) !== normalizeMemberStatus(newStatus);

  const getErrorMessage = (error) => {
    const status = error?.status ?? error?.originalStatus;
    if (status === 401 || status === 403) {
      return "Keine Berechtigung: Der Mitgliedsstatus kann mit diesem Konto nicht geändert werden.";
    }
    return (
      error?.data?.message ||
      "Der Status konnte nicht gespeichert werden. Bitte versuchen Sie es erneut."
    );
  };

  const handleUpdateMember = async () => {
    if (!hasStatusChanged) {
      handleClose();
      return;
    }

    try {
      setErrorMessage("");
      await updateMember({ id: member._id, status: newStatus }).unwrap();

      dispatch(
        setDialog({
          title: "Status erfolgreich geändert",
          type: "SUCCESS",
          data: {
            successText: `Der Status wurde von "${currentStatus.label}" auf "${selectedStatus.label}" geändert.`,
            refetchOnClose: false,
          },
        }),
      );
    } catch (error) {
      setErrorMessage(getErrorMessage(error));
    }
  };

  return (
    <Dialog
      open
      maxWidth="sm"
      fullWidth
      TransitionComponent={Transition}
      keepMounted
      onClose={isLoading ? undefined : () => handleClose()}
      aria-describedby="member-status-dialog-description"
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h6">Status ändern</Typography>
            <Typography variant="body2" color="text.secondary">
              Eine klare Statusführung erleichtert Planung, Abrechnung und Kommunikation.
            </Typography>
          </Box>
          <Button
            color="inherit"
            disabled={isLoading}
            onClick={() => handleClose()}
            sx={{ minWidth: "auto", p: 0.5 }}
          >
            <Iconify icon="icon-park-solid:close-one" width={22} />
          </Button>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2}>
          {!!errorMessage && <Alert severity="error">{errorMessage}</Alert>}

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip
              size="small"
              label={`Aktuell: ${currentStatus.label}`}
              color={currentStatus.color}
              icon={<Iconify icon={currentStatus.icon} width={14} />}
            />
            {hasStatusChanged && (
              <Chip
                size="small"
                label={`Neu: ${selectedStatus.label}`}
                color={selectedStatus.color}
                icon={<Iconify icon={selectedStatus.icon} width={14} />}
              />
            )}
          </Stack>

          <Stack spacing={1.2}>
            {MEMBER_STATUS_OPTIONS.map((option) => {
              const isSelected = option.value === newStatus;
              const isCurrent = option.value === normalizeMemberStatus(member?.status);
              return (
                <Paper
                  key={option.value}
                  variant="outlined"
                  onClick={() => setNewStatus(option.value)}
                  sx={{
                    p: 1.5,
                    cursor: "pointer",
                    borderRadius: 2,
                    borderColor: isSelected ? "primary.main" : "divider",
                    backgroundColor: isSelected ? "action.selected" : "background.paper",
                  }}
                >
                  <Stack direction="row" spacing={1.2} alignItems="flex-start">
                    <Iconify icon={option.icon} width={20} sx={{ mt: 0.2 }} />
                    <Box sx={{ flex: 1 }}>
                      <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                        <Typography variant="subtitle2">{option.label}</Typography>
                        {isCurrent && <Chip size="small" label="Aktuell" />}
                        {isSelected && <Chip size="small" color="primary" label="Ausgewählt" />}
                      </Stack>
                      <Typography variant="body2" color="text.secondary">
                        {option.description}
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>
              );
            })}
          </Stack>

          <Alert severity="info" id="member-status-dialog-description">
            Wirkung: {selectedStatus.effect}
          </Alert>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button disabled={isLoading} onClick={() => handleClose()}>
          Abbrechen
        </Button>
        <Button
          variant="contained"
          onClick={handleUpdateMember}
          disabled={isLoading || !hasStatusChanged}
          startIcon={isLoading ? <CircularProgress color="inherit" size={16} /> : null}
        >
          {isLoading ? "Speichern..." : "Status speichern"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

EditStatusDialog.propTypes = {
  dialog: PropTypes.object,
  handleClose: PropTypes.func,
};

export default EditStatusDialog;
